"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _reactIs = require("react-is");
var _MenuList = require("../MenuList");
var _Popover = _interopRequireDefault(require("../Popover"));
var _createStyles = require("../styles/createStyles");
var _useTheme = require("../styles/useTheme");
var _getReactNodeRef = require("../utilities/getReactNodeRef");
var _setRef = _interopRequireDefault(require("../utilities/setRef"));
const _excluded = ["autoFocus", "children", "classes", "disableAutoFocusItem", "MenuListProps", "onClose", "onEntering", "open", "PaperProps", "PopoverClasses", "transitionDuration", "variant"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const LTR_ORIGIN = {
  vertical: 'top',
  horizontal: 'left'
};
const useStyles = (0, _createStyles.createStyles)(_ref => {
  let {
    theme
  } = _ref;
  return {
    paper: {
      borderRadius: theme.busbud.styles.rounding.lg,
      boxShadow: `${theme.busbud.styles.shadow.regular.lg} !important`,
      // Override until we import Popover and Paper
      // specZ: The maximum height of a simple menu should be one or more rows less than the view
      // height. This ensures a tapable area outside of the simple menu with which to dismiss
      // the menu.
      maxHeight: 'calc(100% - 96px)',
      // Add iOS momentum scrolling.
      WebkitOverflowScrolling: 'touch'
    },
    /* Styles applied to the `List` component via `MenuList`. */
    list: {
      // We disable the focus ring for mouse, touch and keyboard users.
      outline: 0
    }
  };
});
/**
 * Menus display a list of choices on temporary surfaces.
 *
 * [Storybook](https://busbud.github.io/design-system/?path=/docs/components-navigation-menu--page)
 */
const Menu = /*#__PURE__*/_react.default.forwardRef(function Menu(props, forwardedRef) {
  const {
      autoFocus = true,
      children,
      classes: classesOverrides,
      disableAutoFocusItem = false,
      MenuListProps = {},
      onClose,
      onEntering,
      open,
      PaperProps = {},
      PopoverClasses,
      transitionDuration = 'auto',
      variant = 'selectedMenu'
    } = props,
    other = (0, _objectWithoutProperties2.default)(props, _excluded);
  const {
    cx,
    classes
  } = useStyles({
    classesOverrides
  });
  const theme = (0, _useTheme.useTheme)();
  const autoFocusItem = autoFocus && !disableAutoFocusItem && open;
  const menuListActionsRef = _react.default.useRef(null);
  const contentAnchorRef = _react.default.useRef(null);
  const getContentAnchorEl = () => typeof HTMLElement !== 'undefined' && contentAnchorRef.current instanceof HTMLElement ? contentAnchorRef.current : null;
  const handleEntering = (element, isAppearing) => {
    if (menuListActionsRef && menuListActionsRef.current) {
      menuListActionsRef.current.adjustStyleForScrollbar(element, theme);
    }
    if (onEntering) {
      onEntering(element, isAppearing);
    }
  };
  const handleListKeyDown = event => {
    if (event.key === 'Tab') {
      event.preventDefault();
      if (onClose) {
        onClose(event, 'tabKeyDown');
      }
    }
  };

  /**
   * the index of the item should receive focus
   * in a `variant="selectedMenu"` it's the first `selected` item
   * otherwise it's the very first item.
   */
  let activeItemIndex = -1;
  // since we inject focus related props into children we have to do a lookahead
  // to check if there is a `selected` item. We're looking for the last `selected`
  // item and use the first valid item as a fallback
  _react.default.Children.map(children, (child, index) => {
    if (! /*#__PURE__*/_react.default.isValidElement(child)) {
      return;
    }
    if (process.env.NODE_ENV !== 'production') {
      if ((0, _reactIs.isFragment)(child)) {
        console.error(["The Menu component doesn't accept a Fragment as a child.", 'Consider providing an array instead.'].join('\n'));
      }
    }
    if (!child.props.disabled) {
      if (variant === 'selectedMenu' && child.props.selected) {
        activeItemIndex = index;
      } else if (activeItemIndex === -1) {
        activeItemIndex = index;
      }
    }
  });
  const items = _react.default.Children.map(children, (child, index) => {
    if (/*#__PURE__*/_react.default.isValidElement(child) && index === activeItemIndex) {
      return /*#__PURE__*/_react.default.cloneElement(child, {
        ref: instance => {
          contentAnchorRef.current = instance;
          (0, _setRef.default)((0, _getReactNodeRef.getReactNodeRef)(child), instance);
        }
      });
    }
    return child;
  });
  return /*#__PURE__*/_react.default.createElement(_Popover.default, (0, _extends2.default)({
    getContentAnchorEl: getContentAnchorEl,
    anchorOrigin: LTR_ORIGIN,
    classes: PopoverClasses,
    open: open,
    onClose: onClose,
    transitionDuration: transitionDuration,
    transformOrigin: LTR_ORIGIN,
    ref: forwardedRef,
    TransitionProps: {
      onEntering: handleEntering
    },
    PaperProps: _objectSpread(_objectSpread({}, PaperProps), {}, {
      square: true,
      // To avoid CSS override
      classes: _objectSpread(_objectSpread({}, PaperProps.classes), {}, {
        root: classes.paper
      })
    })
  }, other), /*#__PURE__*/_react.default.createElement(_MenuList.MenuList, (0, _extends2.default)({
    "data-testid": "Menu",
    onKeyDown: handleListKeyDown
    // eslint-disable-next-line jsx-a11y/no-autofocus -- TODO: [fix-jsx-a11y]
    ,
    autoFocus: autoFocus && (activeItemIndex === -1 || disableAutoFocusItem),
    autoFocusItem: autoFocusItem,
    variant: variant,
    disablePadding: true // Because MenuItem has margins
  }, MenuListProps, {
    className: cx(classes.list, MenuListProps.className)
  }), items));
});
var _default = exports.default = Menu;